/**
 * Side Menu Composable
 * 管理側邊選單、菜單項堆疊、歷史導航
 * 原始來源: cuora-module/src/machine/side_v2.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/sideMenu.js)
 * - 業務邏輯：保留在 composable 中
 */

import { storeToRefs } from 'pinia'
import { onBeforeUnmount, getCurrentInstance } from 'vue'
import { useSideMenuStore } from '@/stores/sideMenu'
import { gameApi } from '@/api'

const AUTO_UPDATE_INTERVAL = 20000

export const useSideMenu = () => {
  const nuxtApp = useNuxtApp()
  const { $request } = nuxtApp
  const { appSecret } = useRuntimeConfig().public

  const store = useSideMenuStore()
  const { menu, menuItemStack, previousMenuItemStack, historyStack, defaultMenuItemStack, loading, error, lastUpdateTime, getCurrentItem, getCurrentPath, canGoBack } =
    storeToRefs(store)

  // SSR-safe: 使用 nuxtApp context 存儲 timer，避免記憶體洩漏
  // 確保 timer 是單例的，不會因為多個元件使用 composable 而累積
  if (!nuxtApp._sideMenuContext) {
    nuxtApp._sideMenuContext = {
      updateTimer: null,
      // SSR cleanup 方法
      cleanup: () => {
        const ctx = nuxtApp._sideMenuContext
        if (ctx?.updateTimer) {
          clearInterval(ctx.updateTimer)
          ctx.updateTimer = null
        }
      },
    }
  }
  const ctx = nuxtApp._sideMenuContext

  /**
   * 檢查目標項目是否存在於選單樹中
   */
  const isItemInMenu = (targetItem, menuList) => {
    if (!menuList || !Array.isArray(menuList)) {
      return false
    }

    return menuList.reduce((result, currentItem) => {
      if (result) {
        return true
      } else {
        if (currentItem.categoryId === targetItem.categoryId) {
          return true
        } else if (currentItem.sub?.length) {
          return isItemInMenu(targetItem, currentItem.sub)
        } else {
          return false
        }
      }
    }, false)
  }

  /**
   * 遞迴獲取選單的預設路徑（從根到第一個葉節點）
   */
  const getDefaultItem = (node) => {
    if (node.sub?.length > 0 && node.isGame === false) {
      const stack = getDefaultItem(node.sub[0])
      return [node, ...stack]
    } else {
      return [node]
    }
  }

  /**
   * 獲取選單中的第一個館別
   */
  const getFirstCategory = (menu) => {
    if (!menu || !Array.isArray(menu)) {
      return null
    }
    return menu.find((item) => item.isGame === false)
  }

  /**
   * 為選單樹添加層級屬性
   */
  const addLevelProperty = (list, level) => {
    if (!list || !Array.isArray(list)) return

    const nextLevel = level + 1
    for (let i = 0; i < list.length; i++) {
      list[i].level = level

      if (!list[i].isGame && list[i].sub) {
        addLevelProperty(list[i].sub, nextLevel)
      }
    }
  }

  /**
   * 從 API 獲取選單資料
   */
  const getMenu = async () => {
    try {
      const menuData = await $request.send(gameApi.getListRoot({ timestamp: null, appSecret }))

      addLevelProperty(menuData.data, 0)
      store.setMenu(menuData)
      store.setLastUpdateTime(Date.now())

      if (menuData.data?.length > 0) {
        const firstCategory = getFirstCategory(menuData.data)
        // 提前檢查避免無效計算
        if (firstCategory) {
          const stack = getDefaultItem(firstCategory)
          store.setMenuItemStack(stack)
          store.setDefaultMenuItemStack(stack)
        }
      }

      return menuData
    } catch (err) {
      console.error('[useSideMenu] getMenu error:', err)
      throw err
    }
  }

  /**
   * 更新選單資料（增量更新）
   */
  const updateMenu = async () => {
    if (!menu.value) return

    try {
      const menuData = await $request.send(
        gameApi.getListRoot({
          timestamp: menu.value.timestamp,
          appSecret,
        })
      )

      if (!menuData.data?.length) return

      addLevelProperty(menuData.data, 0)
      store.setMenu(menuData)

      const firstCategory = getFirstCategory(menuData.data)
      if (firstCategory) {
        const stack = getDefaultItem(firstCategory)
        store.setDefaultMenuItemStack(stack)
      }

      store.setLastUpdateTime(Date.now())

      return menuData
    } catch (err) {
      console.error('[useSideMenu] updateMenu error:', err)
      throw err
    }
  }

  /**
   * 選擇選單項目
   */
  const selectMenuItem = (inputItem) => {
    const isTopItem = inputItem.level === 0
    const hasSubMenu = inputItem.sub?.length

    if (isTopItem) {
      if (hasSubMenu) {
        store.setPreviousMenuItemStack(menuItemStack.value)
        store.setMenuItemStack([inputItem])
      } else {
        store.setPreviousMenuItemStack([])
        store.setMenuItemStack([inputItem])
        store.setHistoryStack([])
      }
    } else {
      const slicedStack = menuItemStack.value.slice(0, inputItem.level)
      store.setMenuItemStack([...slicedStack, inputItem])
      store.setHistoryStack([])
    }
  }

  /**
   * 添加歷史記錄
   */
  const pushHistory = (data) => {
    store.pushHistoryItem(data)
  }

  /**
   * 移除最後一個歷史記錄
   */
  const popHistory = () => {
    store.popHistoryItem()
  }

  /**
   * 重置到預設狀態
   */
  const reset = () => {
    store.resetToDefault()
  }

  /**
   * 啟動選單系統
   */
  const startMenu = async () => {
    await getMenu()
    startAutoUpdate()
  }

  /**
   * 檢查當前項目是否仍在選單中
   */
  const checkIsInMenu = () => {
    if (!menu.value || !menu.value.data) return false
    const menuItem = menuItemStack.value.slice(-1)[0]
    if (!menuItem) return false
    return isItemInMenu(menuItem, menu.value.data)
  }

  /**
   * 啟動自動更新機制
   */
  const startAutoUpdate = () => {
    // 僅在客戶端執行
    if (!import.meta.client) return

    stopAutoUpdate()

    ctx.updateTimer = setInterval(() => {
      if (checkIsInMenu()) {
        updateMenu()
      }
    }, AUTO_UPDATE_INTERVAL)
  }

  /**
   * 停止自動更新
   */
  const stopAutoUpdate = () => {
    // 僅在客戶端執行
    if (!import.meta.client) return

    if (ctx.updateTimer) {
      clearInterval(ctx.updateTimer)
      ctx.updateTimer = null
    }
  }

  /**
   * 還原先前的選單項目堆疊
   */
  const restoreMenuItemStack = () => {
    store.setPreviousMenuItemStack([])
    store.setMenuItemStack(previousMenuItemStack.value)
  }

  // 元件卸載時自動清理 timer，避免記憶體洩漏
  // 只在有 Vue 元件實例的情況下註冊 hook
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stopAutoUpdate()
    })
  }

  return {
    // State (from store)
    menu,
    menuItemStack,
    historyStack,
    defaultMenuItemStack,
    loading,
    error,
    lastUpdateTime,

    // Computed (from store)
    getCurrentItem,
    getCurrentPath,
    canGoBack,

    // Methods
    startMenu,
    getMenu,
    updateMenu,
    selectMenuItem,
    pushHistory,
    popHistory,
    reset,
    startAutoUpdate,
    stopAutoUpdate,
    isItemInMenu,
    checkIsInMenu,
    restoreMenuItemStack
  }
}
